import { useLocation, useNavigate } from 'react-router-dom';
import { useBolao } from '@/contexts/BolaoContext';
import styles from './BottomNav.module.css';

interface Tab {
  label: string;
  icon: string;
  getPath: (activeBolaoId: string | null) => string;
  matchPrefix: string[];
}

const TABS: Tab[] = [
  {
    label: 'Início',
    icon: '🏠',
    getPath: () => '/',
    matchPrefix: ['/'],
  },
  {
    label: 'Bolões',
    icon: '🏆',
    getPath: () => '/boloes',
    matchPrefix: ['/boloes', '/bolao'],
  },
  {
    label: 'Palpites',
    icon: '⚽',
    getPath: (id) => (id ? `/boloes/${id}/palpites` : '/boloes'),
    matchPrefix: ['/palpites'],
  },
  {
    label: 'Ranking',
    icon: '🏅',
    getPath: (id) => (id ? `/boloes/${id}/ranking` : '/boloes'),
    matchPrefix: ['/ranking'],
  },
  {
    label: 'Perfil',
    icon: '👤',
    getPath: () => '/perfil',
    matchPrefix: ['/perfil'],
  },
];

function isTabActive(tab: Tab, pathname: string, _activeBolaoId: string | null): boolean {
  // Início: exact match only
  if (tab.label === 'Início') {
    return pathname === '/';
  }

  // Palpites: match /boloes/:id/palpites
  if (tab.label === 'Palpites') {
    return /^\/boloes\/[^/]+\/palpites/.test(pathname);
  }

  // Ranking: match /boloes/:id/ranking or /boloes/:id/comparacao or /boloes/:id/estatisticas
  if (tab.label === 'Ranking') {
    return /^\/boloes\/[^/]+\/(ranking|comparacao|estatisticas)/.test(pathname);
  }

  // Bolões: match /boloes or /bolao but not palpites/ranking sub-routes
  if (tab.label === 'Bolões') {
    if (pathname === '/boloes' || pathname.startsWith('/bolao/')) return true;
    if (pathname.startsWith('/boloes/')) {
      // Only match bolão detail/admin/buscar, not palpites/ranking/comparacao/estatisticas
      const subPath = pathname.replace(/^\/boloes\/[^/]+/, '');
      if (
        subPath === '' ||
        subPath === '/admin' ||
        subPath === '/buscar' ||
        pathname === '/boloes/buscar'
      ) {
        return true;
      }
      return false;
    }
    return false;
  }

  // Perfil: prefix match
  if (tab.label === 'Perfil') {
    return pathname.startsWith('/perfil');
  }

  return false;
}

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeBolao } = useBolao();
  const activeBolaoId = activeBolao?.bolaoId ?? null;

  return (
    <nav className={styles.nav} aria-label="Navegação principal">
      {TABS.map((tab) => {
        const active = isTabActive(tab, location.pathname, activeBolaoId);
        return (
          <button
            key={tab.label}
            type="button"
            className={`${styles.tab} ${active ? styles.tabActive : ''}`}
            onClick={() => navigate(tab.getPath(activeBolaoId))}
            aria-current={active ? 'page' : undefined}
            aria-label={tab.label}
          >
            <span className={styles.icon} aria-hidden="true">
              {tab.icon}
            </span>
            <span className={styles.label}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
